import { useState } from "react";
import { ArrowRight, CheckCircle2, MapPin, Car, Footprints } from "lucide-react";
import { Button } from "./ui/button";
import { motion } from "motion/react";
import { LoginSignup } from "./LoginSignup";
import { BookingPage } from "./BookingPage";
import { useAuth } from "../contexts/AuthContext";
import { CONTACT } from "../config/contact";
import hostelImage from "../assets/mainbackground.jpg";

const HERO_HIGHLIGHTS = [
  "24/7 Electricity & Water",
  "Starlink WiFi",
  "24/7 Security",
  "Games (Snooker/Table Soccer)",
];

export function Hero() {
  const { user } = useAuth();
  const [showLoginSignup, setShowLoginSignup] = useState(false);
  const [showBookingPage, setShowBookingPage] = useState(false);

  const handleExploreRooms = () => {
    if (user) {
      setShowBookingPage(true);
    } else {
      setShowLoginSignup(true);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" aria-label="Hero section">
      {/* Background photography — the building itself carries the hero */}
      <div className="absolute inset-0">
        <img
          src={hostelImage}
          alt="The Majesty Hostel building in Koforidua"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
        {/* Legibility overlay: darkest at the base where the copy sits, photo shows through up top */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f1f3d] via-[#0f1f3d]/85 to-transparent"></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-white mb-6 text-5xl sm:text-6xl lg:text-7xl"
          style={{ lineHeight: 1.06 }}
        >
          Welcome to <span className="text-[#d4af37]">Majesty Hostel</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
          className="text-white/80 text-xl lg:text-2xl max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          Secure, well-run student accommodation in Koforidua, Ghana — with round-the-clock
          power, water and security, and Starlink internet throughout.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.24, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap gap-4 justify-center mb-12"
        >
          <Button
            size="lg"
            variant="gold"
            className="group text-lg px-8 py-6 rounded-lg shadow-lg"
            onClick={handleExploreRooms}
          >
            Explore Rooms
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="border border-white/30 text-white bg-transparent hover:bg-white/10 text-lg px-8 py-6 rounded-lg"
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
          >
            Contact Us
          </Button>
        </motion.div>

        {/* Key differentiators — a quiet line, not a pile of glass pills */}
        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="flex flex-wrap gap-4 justify-center items-center text-white/80"
          style={{ columnGap: "1.5rem", rowGap: "0.5rem" }}
        >
          {HERO_HIGHLIGHTS.map((feature) => (
            <li key={feature} className="inline-flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#d4af37]" aria-hidden="true" />
              <span>{feature}</span>
            </li>
          ))}
        </motion.ul>

        {/* Location — distance from campus with travel times */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="flex flex-wrap items-center justify-center gap-2 text-white"
          style={{ marginTop: "1.75rem", columnGap: "0.75rem", rowGap: "0.5rem" }}
        >
          <span className="inline-flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#d4af37]" aria-hidden="true" />
            <span>{CONTACT.campus.distanceKm} km from {CONTACT.campus.name}</span>
          </span>
          <span className="inline-flex items-center gap-3 text-white/70 text-sm">
            <span className="inline-flex items-center gap-1.5">
              <Car className="w-4 h-4" aria-hidden="true" />
              {CONTACT.campus.byCar}
            </span>
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Footprints className="w-4 h-4" aria-hidden="true" />
              {CONTACT.campus.byWalk}
            </span>
          </span>
          <a
            href={CONTACT.campus.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#d4af37] transition-colors"
            style={{ textDecoration: "underline", textUnderlineOffset: "3px" }}
            aria-label="View Majesty Hostel location on Google Maps"
          >
            View map
          </a>
        </motion.div>
      </div>

      <LoginSignup
        open={showLoginSignup}
        onOpenChange={setShowLoginSignup}
        onSuccess={() => setShowBookingPage(true)}
      />
      <BookingPage open={showBookingPage} onOpenChange={setShowBookingPage} />
    </section>
  );
}
